import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { getProduct, getImageUrl } from "@/lib/api";
import {
  ArrowRight, Check, TrendingDown, AlertTriangle,
  XCircle, Zap, Users, Shield, Star, Clock, Sparkles, BookOpen,
  ArrowLeft, Play, Target, Lightbulb, Rocket, Award, Heart,
  Monitor, Palette, Code, Gift, Percent, Globe, MessageCircle,
  ChevronRight, Timer, Layers, Briefcase, GraduationCap
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

const CLIP_PATH = "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)";

const ICON_MAP = {
  "trending-down": TrendingDown, "alert": AlertTriangle, "x-circle": XCircle,
  "zap": Zap, "users": Users, "shield": Shield, "star": Star, "clock": Clock,
  "sparkles": Sparkles, "book": BookOpen, "check": Check, "play": Play,
  "target": Target, "lightbulb": Lightbulb, "rocket": Rocket, "award": Award,
  "heart": Heart, "monitor": Monitor, "palette": Palette, "code": Code,
  "gift": Gift, "percent": Percent, "globe": Globe, "message": MessageCircle,
  "timer": Timer, "layers": Layers, "briefcase": Briefcase, "graduation": GraduationCap,
};

function getIcon(name, fallback) {
  return ICON_MAP[name] || fallback || Sparkles;
}

function RenderTitle({ text, accentColor }) {
  if (!text) return null;
  return text.split('*').map((part, i) =>
    i % 2 === 1 ? <span key={i} style={{ color: accentColor }}>{part}</span> : part
  );
}

// ─── HERO ───
function HeroSection({ section, accentColor, product }) {
  const videoEmbed = section.embed_url ? (
    <div className="rounded-2xl lg:rounded-3xl overflow-hidden border border-white/10 shadow-2xl" style={{ boxShadow: `0 0 80px ${accentColor}15` }}>
      <div style={{ position: 'relative', paddingTop: '56.25%' }}>
        <iframe
          src={section.embed_url}
          loading="lazy"
          style={{ border: 0, position: 'absolute', top: 0, height: '100%', width: '100%' }}
          allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
          allowFullScreen={true}
          title="Video"
        />
      </div>
    </div>
  ) : null;

  const imageBlock = (!section.embed_url && (section.image_url || product.image_url)) ? (
    <div className="rounded-2xl lg:rounded-3xl overflow-hidden border border-white/10">
      <img src={getImageUrl(section.image_url || product.image_url)} alt={product.title} className="w-full h-auto object-cover" data-testid="landing-hero-image" />
    </div>
  ) : null;

  const media = videoEmbed || imageBlock;

  const handleScrollToInfo = (e) => {
    e.preventDefault();
    const target = document.querySelector('[data-testid="landing-problems"], [data-testid="landing-features"], [data-testid="landing-solution"]');
    if (target) target.scrollIntoView({ behavior: 'smooth' });
  };

  const ctaButtons = (section.cta_buttons || product.cta_buttons || []);

  return (
    <section className="relative isolate px-6 md:px-12 pt-32 pb-20 md:pt-40 md:pb-28" data-testid="landing-hero">
      <div className="max-w-7xl mx-auto">
        {/* ── DESKTOP: side-by-side ── */}
        <div className="hidden lg:grid lg:grid-cols-2 gap-20 items-center">
          <div>
            {section.label && (
              <span className="text-sm font-semibold uppercase tracking-wider mb-4 block" style={{ color: accentColor }}>{section.label}</span>
            )}
            <h1 className="font-['Outfit'] text-5xl md:text-7xl font-black tracking-tighter text-white leading-[0.95]" data-testid="landing-hero-title">
              <RenderTitle text={section.title || product.title} accentColor={accentColor} />
            </h1>
            {section.description && (
              <p className="mt-6 text-lg text-white/50 leading-relaxed max-w-xl">{section.description}</p>
            )}
            {section.bullets?.length > 0 && (
              <ul className="mt-6 space-y-2">
                {section.bullets.map((b, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/70 text-base">
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: accentColor }} />{b}
                  </li>
                ))}
              </ul>
            )}
            {section.note && <p className="mt-4 text-sm text-white/30">{section.note}</p>}
            <div className="mt-8 flex flex-wrap gap-3">
              {ctaButtons.map((btn, idx) => (
                idx === 0 ? (
                  <a key={idx} href={btn.url} target="_blank" rel="noopener noreferrer" data-testid={`hero-cta-${idx}`}>
                    <button className="px-8 py-3.5 rounded-full text-white font-semibold text-base transition-all hover:scale-105 active:scale-95 flex items-center gap-2" style={{ background: accentColor }}>
                      {btn.text} <ArrowRight className="w-4 h-4" />
                    </button>
                  </a>
                ) : (
                  <button key={idx} onClick={handleScrollToInfo} className="px-8 py-3.5 rounded-full font-semibold text-base transition-all hover:scale-105 active:scale-95 flex items-center gap-2 border text-white/70 hover:text-white cursor-pointer" style={{ borderColor: `${accentColor}40` }} data-testid={`hero-cta-${idx}`}>
                    {btn.text}
                  </button>
                )
              ))}
            </div>
          </div>
          {media && <div className="relative">{media}</div>}
        </div>

        {/* ── MOBILE: stacked – Title → Video → Text → CTA ── */}
        <div className="lg:hidden">
          {section.label && (
            <span className="text-sm font-semibold uppercase tracking-wider mb-4 block" style={{ color: accentColor }}>{section.label}</span>
          )}
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-black tracking-tighter text-white leading-[0.95]">
            <RenderTitle text={section.title || product.title} accentColor={accentColor} />
          </h1>

          {/* Video/Image between title and description on mobile */}
          {media && <div className="mt-6">{media}</div>}

          {section.description && (
            <p className="mt-6 text-base text-white/50 leading-relaxed">{section.description}</p>
          )}
          {section.bullets?.length > 0 && (
            <ul className="mt-5 space-y-2">
              {section.bullets.map((b, i) => (
                <li key={i} className="flex items-center gap-3 text-white/70 text-sm">
                  <Check className="w-4 h-4 flex-shrink-0" style={{ color: accentColor }} />{b}
                </li>
              ))}
            </ul>
          )}
          {section.note && <p className="mt-3 text-sm text-white/30">{section.note}</p>}
          <div className="mt-6 flex flex-wrap gap-3">
            {ctaButtons.map((btn, idx) => (
              idx === 0 ? (
                <a key={idx} href={btn.url} target="_blank" rel="noopener noreferrer" data-testid={`hero-cta-mobile-${idx}`}>
                  <button className="px-8 py-3.5 rounded-full text-white font-semibold text-base transition-all hover:scale-105 active:scale-95 flex items-center gap-2" style={{ background: accentColor }}>
                    {btn.text} <ArrowRight className="w-4 h-4" />
                  </button>
                </a>
              ) : (
                <button key={idx} onClick={handleScrollToInfo} className="px-8 py-3.5 rounded-full font-semibold text-base transition-all hover:scale-105 active:scale-95 flex items-center gap-2 border text-white/70 hover:text-white cursor-pointer" style={{ borderColor: `${accentColor}40` }} data-testid={`hero-cta-mobile-${idx}`}>
                  {btn.text}
                </button>
              )
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── VIDEO EMBED ───
function VideoSection({ section, accentColor }) {
  return (
    <section className="px-6 md:px-12 py-16 md:py-24" data-testid="landing-video">
      <div className="max-w-5xl mx-auto">
        {section.label && (
          <p className="text-center text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: accentColor }}>{section.label}</p>
        )}
        {section.title && (
          <h2 className="font-['Outfit'] text-2xl md:text-4xl font-black tracking-tight text-white text-center mb-8">
            <RenderTitle text={section.title} accentColor={accentColor} />
          </h2>
        )}
        <div className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl" style={{ boxShadow: `0 0 80px ${accentColor}15` }}>
          <div style={{ position: 'relative', paddingTop: '56.25%' }}>
            <iframe
              src={section.embed_url}
              loading="lazy"
              style={{ border: 0, position: 'absolute', top: 0, height: '100%', width: '100%' }}
              allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
              allowFullScreen={true}
              title="Video"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── INTRO / ABOUT (Tina Section) ───
function IntroSection({ section, accentColor }) {
  return (
    <section className="px-4 sm:px-6 md:px-12 py-16 md:py-24" data-testid="landing-intro">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-['Outfit'] text-2xl sm:text-3xl md:text-5xl font-black tracking-tight text-white leading-tight">
          <RenderTitle text={section.title} accentColor={accentColor} />
        </h2>
        {section.description && (
          <p className="mt-6 sm:mt-8 text-white/70 text-base sm:text-lg md:text-xl leading-relaxed whitespace-pre-line max-w-3xl mx-auto">
            {section.description}
          </p>
        )}
        {section.name && (
          <p className="mt-6 text-lg font-semibold" style={{ color: accentColor }}>
            – {section.name}
          </p>
        )}
      </div>
    </section>
  );
}

// ─── PROBLEMS ───
function ProblemsSection({ section, accentColor }) {
  const fallbackIcons = [TrendingDown, AlertTriangle, XCircle];
  return (
    <section className="px-6 md:px-12 py-20 md:py-28" data-testid="landing-problems">
      <div className="max-w-7xl mx-auto text-center">
        <h2 className="font-['Outfit'] text-3xl md:text-5xl font-black tracking-tight text-white">
          <RenderTitle text={section.title || "DAS PROBLEM"} accentColor={accentColor} />
        </h2>
        {section.subtitle && <p className="mt-4 text-white/40 text-lg max-w-2xl mx-auto">{section.subtitle}</p>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {(section.cards || []).map((card, idx) => {
            const Icon = getIcon(card.icon, fallbackIcons[idx % 3]);
            return (
              <div key={idx} className="rounded-2xl p-6 md:p-8 border text-left transition-all hover:-translate-y-1" style={{ borderColor: `${accentColor}25`, background: `${accentColor}08` }} data-testid={`problem-card-${idx}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: `${accentColor}15` }}>
                  <Icon className="w-6 h-6" style={{ color: accentColor }} />
                </div>
                <h3 className="font-['Outfit'] font-bold text-white text-lg md:text-xl uppercase tracking-wide">{card.title}</h3>
                <p className="mt-2 text-white/40 text-sm md:text-base leading-relaxed">{card.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── SOLUTION / MISSION ───
function SolutionSection({ section, accentColor }) {
  return (
    <section className="px-6 md:px-12 py-20 md:py-28" data-testid="landing-solution">
      <div className="max-w-5xl mx-auto text-center">
        <h2 className="font-['Outfit'] text-3xl md:text-5xl font-black tracking-tight text-white">
          <RenderTitle text={section.title} accentColor={accentColor} />
        </h2>
        {section.subtitle && <p className="mt-4 text-white/40 text-lg max-w-2xl mx-auto">{section.subtitle}</p>}
        {section.mission && (
          <div className="mt-10 rounded-2xl border border-white/[0.08] bg-white/[0.03] p-8 md:p-10">
            <h3 className="font-['Outfit'] text-sm font-bold uppercase tracking-wider mb-4" style={{ color: accentColor }}>
              {section.mission_label || "UNSERE MISSION"}
            </h3>
            <p className="text-white/60 text-base md:text-lg leading-relaxed">{section.mission}</p>
          </div>
        )}
        {section.highlights?.length > 0 && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {section.highlights.map((h, i) => (
              <div key={i} className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5 text-left">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ background: `${accentColor}15` }}>
                  {(() => { const I = getIcon(h.icon); return <I className="w-4 h-4" style={{ color: accentColor }} />; })()}
                </div>
                <p className="text-white/70 text-sm font-medium">{h.text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── HIGHLIGHT / STAT BLOCK (e.g. "72h Setup") ───
function HighlightSection({ section, accentColor }) {
  return (
    <section className="px-6 md:px-12 py-20 md:py-28" data-testid="landing-highlight">
      <div className="max-w-5xl mx-auto">
        <div className="rounded-3xl border p-8 md:p-12 relative overflow-hidden" style={{ borderColor: `${accentColor}20`, background: `${accentColor}06` }}>
          {section.badge && (
            <Badge className="mb-4 border-none text-white text-xs" style={{ background: accentColor }}>{section.badge}</Badge>
          )}
          <h2 className="font-['Outfit'] text-2xl md:text-4xl font-black tracking-tight text-white">
            <RenderTitle text={section.title} accentColor={accentColor} />
          </h2>
          {section.description && <p className="mt-3 text-white/40 text-base md:text-lg max-w-2xl leading-relaxed">{section.description}</p>}
          {section.stat_value && (
            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-['Outfit'] text-6xl md:text-7xl font-black" style={{ color: accentColor }}>{section.stat_value}</span>
              {section.stat_label && <span className="text-white/50 text-base">{section.stat_label}</span>}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── PILLS / TAGS (e.g. "WAS IM SYSTEM ENTHALTEN IST") ───
function PillsSection({ section, accentColor }) {
  return (
    <section className="px-6 md:px-12 py-20 md:py-28" data-testid="landing-pills">
      <div className="max-w-5xl mx-auto text-center">
        <h2 className="font-['Outfit'] text-3xl md:text-5xl font-black tracking-tight text-white">
          <RenderTitle text={section.title} accentColor={accentColor} />
        </h2>
        {section.subtitle && <p className="mt-4 text-white/40 text-lg max-w-2xl mx-auto">{section.subtitle}</p>}
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          {(section.items || []).map((item, i) => (
            <span key={i} className="px-5 py-2.5 rounded-full text-sm font-medium border transition-all hover:scale-105" style={{ borderColor: `${accentColor}30`, color: accentColor, background: `${accentColor}10` }}>
              {item}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── BONUS CARDS ───
function BonusSection({ section, accentColor }) {
  return (
    <section className="px-6 md:px-12 py-20 md:py-28" data-testid="landing-bonus">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {(section.cards || []).map((card, idx) => (
            <div key={idx} className="rounded-2xl border p-8 relative overflow-hidden" style={{ borderColor: `${accentColor}25`, background: `${accentColor}08` }} data-testid={`bonus-card-${idx}`}>
              {card.badge && (
                <Badge className="mb-3 border-none text-white text-xs" style={{ background: accentColor }}>{card.badge}</Badge>
              )}
              <h3 className="font-['Outfit'] font-bold text-white text-xl md:text-2xl">{card.title}</h3>
              <p className="mt-2 text-white/40 text-sm md:text-base leading-relaxed">{card.description}</p>
              {card.stat_value && (
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-['Outfit'] text-4xl font-black" style={{ color: accentColor }}>{card.stat_value}</span>
                  {card.stat_label && <span className="text-white/40 text-sm">{card.stat_label}</span>}
                </div>
              )}
              {card.note && <p className="mt-3 text-white/30 text-xs">{card.note}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── MODULES / LEARNING PATH ───
function ModulesSection({ section, accentColor }) {
  return (
    <section className="px-6 md:px-12 py-20 md:py-28" data-testid="landing-modules">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          {section.label && <p className="text-sm font-semibold uppercase tracking-wider mb-2" style={{ color: accentColor }}>{section.label}</p>}
          <h2 className="font-['Outfit'] text-3xl md:text-5xl font-black tracking-tight text-white">
            <RenderTitle text={section.title} accentColor={accentColor} />
          </h2>
          {section.subtitle && <p className="mt-4 text-white/40 text-lg max-w-2xl mx-auto">{section.subtitle}</p>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
          {(section.modules || []).map((mod, idx) => (
            <div key={idx} className="rounded-xl border border-white/[0.08] bg-white/[0.03] overflow-hidden transition-all hover:-translate-y-1 hover:border-white/[0.15] group" data-testid={`module-${idx}`}>
              {mod.image_url ? (
                <img src={getImageUrl(mod.image_url)} alt={mod.title} className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-105" />
              ) : (
                <div className="aspect-video flex items-center justify-center" style={{ background: `${accentColor}10` }}>
                  <span className="font-['Outfit'] text-2xl font-black" style={{ color: `${accentColor}40` }}>{String(idx + 1).padStart(2, '0')}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── TESTIMONIALS / SUCCESS STORIES ───
function TestimonialsSection({ section, accentColor }) {
  const [selectedImage, setSelectedImage] = useState(null);
  
  return (
    <section className="px-4 sm:px-6 md:px-12 py-16 md:py-28 overflow-hidden" data-testid="landing-testimonials">
      <div className="max-w-7xl mx-auto text-center">
        <h2 className="font-['Outfit'] text-2xl sm:text-3xl md:text-5xl font-black tracking-tight text-white">
          <RenderTitle text={section.title} accentColor={accentColor} />
        </h2>
        {section.subtitle && <p className="mt-3 sm:mt-4 text-white/40 text-base sm:text-lg max-w-2xl mx-auto">{section.subtitle}</p>}
        
        {/* Grid layout for mobile, scrolling for desktop */}
        {(section.rows || []).map((row, rowIdx) => (
          <div key={rowIdx} className="mt-6 sm:mt-8">
            {/* Mobile: Grid layout */}
            <div className="grid grid-cols-2 gap-3 sm:hidden">
              {row.map((img, i) => (
                <button 
                  key={i} 
                  onClick={() => setSelectedImage(getImageUrl(img))}
                  className="focus:outline-none focus:ring-2 focus:ring-pink-500 rounded-xl transition-transform active:scale-95"
                >
                  <img 
                    src={getImageUrl(img)} 
                    alt={`Erfolg ${i + 1}`} 
                    className="w-full h-auto rounded-xl border border-white/20 shadow-lg" 
                  />
                </button>
              ))}
            </div>
            {/* Desktop: Scrolling animation */}
            <div className="hidden sm:flex gap-4 animate-scroll-left overflow-hidden">
              {[...row, ...row].map((img, i) => (
                <button 
                  key={i}
                  onClick={() => setSelectedImage(getImageUrl(img))}
                  className="focus:outline-none hover:scale-105 transition-transform cursor-pointer flex-shrink-0"
                >
                  <img 
                    src={getImageUrl(img)} 
                    alt={`Erfolg ${i + 1}`} 
                    className="w-60 h-auto rounded-xl border border-white/10" 
                  />
                </button>
              ))}
            </div>
          </div>
        ))}
        {section.note && <p className="mt-6 text-white/30 text-xs px-4">{section.note}</p>}
      </div>
      
      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white/70 hover:text-white text-xl p-2"
            onClick={() => setSelectedImage(null)}
          >
            ✕
          </button>
          <img 
            src={selectedImage} 
            alt="Erfolg vergrößert" 
            className="max-w-full max-h-[90vh] rounded-xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}

// ─── TEAM ───
function TeamSection({ section, accentColor }) {
  return (
    <section className="px-6 md:px-12 py-20 md:py-28" data-testid="landing-team">
      <div className="max-w-5xl mx-auto text-center">
        <h2 className="font-['Outfit'] text-3xl md:text-5xl font-black tracking-tight text-white">
          <RenderTitle text={section.title} accentColor={accentColor} />
        </h2>
        {section.subtitle && <p className="mt-4 text-white/40 text-lg max-w-2xl mx-auto">{section.subtitle}</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12 max-w-3xl mx-auto">
          {(section.members || []).map((m, idx) => (
            <div key={idx} className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 text-left" data-testid={`team-${idx}`}>
              {m.image_url && (
                <img src={getImageUrl(m.image_url)} alt={m.name} className="w-16 h-16 rounded-xl object-cover mb-4" />
              )}
              <h3 className="font-['Outfit'] font-bold text-white text-lg">{m.name}</h3>
              <p className="text-sm font-medium mb-3" style={{ color: accentColor }}>{m.role}</p>
              {m.bio && <p className="text-white/40 text-sm leading-relaxed">{m.bio}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── STATS BAR ───
function StatsSection({ section, accentColor }) {
  return (
    <section className="px-6 md:px-12 py-12" data-testid="landing-stats">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {(section.items || []).map((item, idx) => (
            <div key={idx} className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5 text-center" data-testid={`stat-${idx}`}>
              <span className="block font-['Outfit'] text-sm font-bold uppercase tracking-wider" style={{ color: accentColor }}>{item.label}</span>
              <span className="block text-white/60 text-xs mt-1">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── CUSTOM PLATFORM / PROMO ───
function PlatformSection({ section, accentColor }) {
  return (
    <section className="px-6 md:px-12 py-20 md:py-28" data-testid="landing-platform">
      <div className="max-w-5xl mx-auto">
        {section.badge && (
          <p className="text-center text-xs font-medium text-white/30 mb-3 uppercase tracking-wider">{section.badge}</p>
        )}
        <h2 className="font-['Outfit'] text-3xl md:text-5xl font-black tracking-tight text-white text-center">
          <RenderTitle text={section.title} accentColor={accentColor} />
        </h2>
        {section.subtitle && <p className="mt-4 text-white/40 text-lg max-w-2xl mx-auto text-center">{section.subtitle}</p>}
        <div className="mt-12 space-y-4">
          {(section.cards || []).map((card, idx) => (
            <div key={idx} className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 md:p-8 transition-all hover:border-white/[0.15]" data-testid={`platform-card-${idx}`}>
              <h3 className="font-['Outfit'] font-bold text-white text-lg">{card.title}</h3>
              <p className="mt-2 text-white/40 text-sm md:text-base leading-relaxed">{card.description}</p>
            </div>
          ))}
        </div>
        {section.note && <p className="mt-6 text-white/30 text-xs text-center">{section.note}</p>}
        {section.cta_url && (
          <div className="mt-8 text-center">
            <a href={section.cta_url} target="_blank" rel="noopener noreferrer" data-testid="platform-cta">
              <button className="px-8 py-3.5 rounded-full text-white font-semibold transition-all hover:scale-105" style={{ background: accentColor }}>
                {section.cta_text || "Mehr erfahren"} <ArrowRight className="w-4 h-4 inline ml-2" />
              </button>
            </a>
            {section.cta_note && <p className="mt-2 text-white/30 text-xs">{section.cta_note}</p>}
          </div>
        )}
      </div>
    </section>
  );
}

// ─── FEATURES (existing) ───
function FeaturesSection({ section, accentColor }) {
  return (
    <section className="px-6 md:px-12 py-20 md:py-28" data-testid="landing-features">
      <div className="max-w-7xl mx-auto text-center">
        <h2 className="font-['Outfit'] text-3xl md:text-5xl font-black tracking-tight text-white">
          <RenderTitle text={section.title || "WAS DU BEKOMMST"} accentColor={accentColor} />
        </h2>
        {section.subtitle && <p className="mt-4 text-white/40 text-lg max-w-2xl mx-auto">{section.subtitle}</p>}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {(section.columns || []).map((col, idx) => (
            <div key={idx} className="rounded-2xl p-6 md:p-8 border border-white/[0.08] bg-white/[0.03] text-left" data-testid={`feature-col-${idx}`}>
              <h3 className="font-['Outfit'] font-bold text-sm uppercase tracking-wider mb-5" style={{ color: accentColor }}>{col.title}</h3>
              <ul className="space-y-3">
                {(col.items || []).map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/60 text-sm">
                    <Check className="w-4 h-4 flex-shrink-0" style={{ color: accentColor }} />{item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── BENEFITS (existing) ───
function BenefitsSection({ section, accentColor }) {
  return (
    <section className="px-6 md:px-12 py-20 md:py-28" data-testid="landing-benefits">
      <div className="max-w-7xl mx-auto">
        <h2 className="font-['Outfit'] text-3xl md:text-5xl font-black tracking-tight text-white text-center">
          <RenderTitle text={section.title || "Das erwartet dich"} accentColor={accentColor} />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {(section.items || []).map((item, idx) => {
            const Icon = getIcon(item.icon);
            return (
              <div key={idx} className="rounded-2xl p-6 border border-white/[0.08] bg-white/[0.03] transition-all hover:-translate-y-1 hover:border-white/[0.15]" data-testid={`benefit-${idx}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: `${accentColor}15` }}>
                  <Icon className="w-5 h-5" style={{ color: accentColor }} />
                </div>
                <h3 className="font-['Outfit'] font-bold text-white text-base">{item.title}</h3>
                <p className="mt-2 text-white/40 text-sm leading-relaxed">{item.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── PRICING (existing) ───
function PricingSection({ section, accentColor }) {
  return (
    <section className="px-6 md:px-12 py-20 md:py-28" data-testid="landing-pricing">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="font-['Outfit'] text-3xl md:text-5xl font-black tracking-tight text-white">
          <RenderTitle text={section.title || "PRICING"} accentColor={accentColor} />
        </h2>
        <div className="mt-12 rounded-3xl border p-8 md:p-10 text-left relative overflow-hidden" style={{ borderColor: `${accentColor}30`, background: `${accentColor}08` }}>
          {section.badge && <Badge className="absolute top-4 right-4 border-none text-white text-xs" style={{ background: accentColor }}>{section.badge}</Badge>}
          <h3 className="font-['Outfit'] font-bold text-white text-xl md:text-2xl">{section.product_name}</h3>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="font-['Outfit'] text-5xl md:text-6xl font-black text-white">{section.price}</span>
            <span className="text-white/40 text-sm">{section.period}</span>
          </div>
          {section.alt_price && <p className="mt-2 text-white/40 text-sm">{section.alt_price}</p>}
          <ul className="mt-8 space-y-3">
            {(section.features || []).map((f, i) => (
              <li key={i} className="flex items-center gap-3 text-white/70 text-sm">
                <Check className="w-4 h-4 flex-shrink-0" style={{ color: accentColor }} />{f}
              </li>
            ))}
          </ul>
          {section.cta_url && (
            <a href={section.cta_url} target="_blank" rel="noopener noreferrer" className="mt-8 block" data-testid="pricing-cta">
              <button className="w-full px-8 py-4 rounded-full text-white font-semibold text-base transition-all hover:scale-[1.02] active:scale-[0.98]" style={{ background: accentColor }}>
                {section.cta_text || "Jetzt starten"}
              </button>
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── ADVANTAGES ───
function AdvantagesSection({ section, accentColor }) {
  return (
    <section className="px-6 md:px-12 py-20 md:py-28" data-testid="landing-advantages">
      <div className="max-w-5xl mx-auto text-center">
        <h2 className="font-['Outfit'] text-3xl md:text-5xl font-black tracking-tight text-white">
          <RenderTitle text={section.title || "DEINE VORTEILE"} accentColor={accentColor} />
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
          {(section.items || []).map((item, idx) => (
            <div key={idx} className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5" data-testid={`advantage-${idx}`}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-3" style={{ background: `${accentColor}15` }}>
                {(() => { const I = getIcon(item.icon); return <I className="w-5 h-5" style={{ color: accentColor }} />; })()}
              </div>
              <h4 className="font-['Outfit'] font-bold text-white text-sm">{item.title}</h4>
              <p className="text-white/40 text-xs mt-1">{item.subtitle}</p>
            </div>
          ))}
        </div>
        {section.bottom_text && (
          <p className="mt-8 text-white/50 text-base">{section.bottom_text}</p>
        )}
      </div>
    </section>
  );
}

// ─── CTA (existing) ───
function CTASection({ section, accentColor }) {
  return (
    <section className="px-6 md:px-12 py-20 md:py-28" data-testid="landing-cta">
      <div className="max-w-3xl mx-auto text-center">
        <h2 className="font-['Outfit'] text-3xl md:text-5xl font-black tracking-tight text-white">
          <RenderTitle text={section.title || "BEREIT?"} accentColor={accentColor} />
        </h2>
        {section.subtitle && <p className="mt-4 text-white/40 text-lg max-w-xl mx-auto">{section.subtitle}</p>}
        {section.cta_url && (
          <a href={section.cta_url} target="_blank" rel="noopener noreferrer" className="mt-8 inline-block" data-testid="bottom-cta">
            <button className="px-10 py-4 rounded-full text-white font-semibold text-base transition-all hover:scale-105 active:scale-95" style={{ background: accentColor }}>
              {section.cta_text || "Jetzt starten"} <ArrowRight className="w-4 h-4 inline ml-2" />
            </button>
          </a>
        )}
      </div>
    </section>
  );
}

// ─── STEPS (existing) ───
function StepsSection({ section, accentColor }) {
  return (
    <section className="px-6 md:px-12 py-20 md:py-28" data-testid="landing-steps">
      <div className="max-w-7xl mx-auto text-center">
        <h2 className="font-['Outfit'] text-3xl md:text-5xl font-black tracking-tight text-white">
          <RenderTitle text={section.title || "SO FUNKTIONIERT'S"} accentColor={accentColor} />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
          {(section.steps || []).map((step, idx) => (
            <div key={idx} className="text-left" data-testid={`step-${idx}`}>
              <span className="font-['Outfit'] text-5xl font-black" style={{ color: `${accentColor}30` }}>0{idx + 1}</span>
              <h3 className="font-['Outfit'] font-bold text-white text-lg mt-2">{step.title}</h3>
              <p className="mt-2 text-white/40 text-sm leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
        {section.cta_url && (
          <a href={section.cta_url} target="_blank" rel="noopener noreferrer" className="mt-10 inline-block">
            <button className="px-8 py-3.5 rounded-full text-white font-semibold transition-all hover:scale-105" style={{ background: accentColor }}>
              {section.cta_text || "Jetzt starten"}
            </button>
          </a>
        )}
      </div>
    </section>
  );
}

// ─── SECTION MAP ───
const SECTION_MAP = {
  hero: HeroSection,
  intro: IntroSection,
  video: VideoSection,
  problems: ProblemsSection,
  solution: SolutionSection,
  highlight: HighlightSection,
  features: FeaturesSection,
  pills: PillsSection,
  bonus: BonusSection,
  benefits: BenefitsSection,
  modules: ModulesSection,
  testimonials: TestimonialsSection,
  team: TeamSection,
  stats: StatsSection,
  platform: PlatformSection,
  advantages: AdvantagesSection,
  pricing: PricingSection,
  cta: CTASection,
  steps: StepsSection,
};

function SimpleLanding({ product, accentColor }) {
  return (
    <HeroSection section={{ title: product.title, description: product.description, image_url: product.image_url, cta_buttons: product.cta_buttons }} accentColor={accentColor} product={product} />
  );
}

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProduct(id).then(p => { setProduct(p); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]" data-testid="product-loading">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]" data-testid="product-not-found">
        <div className="text-center">
          <h2 className="font-['Outfit'] text-2xl font-bold text-white mb-2">Produkt nicht gefunden</h2>
          <Link to="/" className="text-indigo-400 hover:text-indigo-300 text-sm font-medium">Zurück zur Startseite</Link>
        </div>
      </div>
    );
  }

  const accentColor = product.accent_color || "#6366f1";
  const sections = product.landing_sections;
  const hasSections = sections && sections.length > 0;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white relative overflow-x-hidden" data-testid="product-page">
      {/* Gradient blobs */}
      <div aria-hidden="true" className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80">
        <div style={{ clipPath: CLIP_PATH, background: `linear-gradient(to top right, ${accentColor}90, ${accentColor}50)` }} className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] max-w-none -translate-x-1/2 rotate-[30deg] opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]" />
      </div>
      <div aria-hidden="true" className="absolute inset-x-0 top-[50%] -z-10 transform-gpu overflow-hidden blur-3xl">
        <div style={{ clipPath: CLIP_PATH, background: `linear-gradient(to top right, ${accentColor}60, ${accentColor}30)` }} className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] max-w-none -translate-x-1/2 opacity-20 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]" />
      </div>
      <div aria-hidden="true" className="absolute inset-x-0 bottom-0 -z-10 transform-gpu overflow-hidden blur-3xl">
        <div style={{ clipPath: CLIP_PATH, background: `linear-gradient(to top right, ${accentColor}70, ${accentColor}40)` }} className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] max-w-none -translate-x-1/2 rotate-[30deg] opacity-20 sm:left-[calc(50%-20rem)] sm:w-[72.1875rem]" />
      </div>

      {/* Sticky nav */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-medium text-white/50 hover:text-white transition-colors" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </Link>
          <span className="font-['Outfit'] font-bold text-white text-sm">{product.title}</span>
          {product.cta_buttons?.[0] && (
            <a href={product.cta_buttons[0].url} target="_blank" rel="noopener noreferrer" data-testid="nav-cta">
              <button className="px-5 py-2 rounded-full text-white text-sm font-semibold transition-all hover:scale-105" style={{ background: accentColor }}>
                {product.cta_buttons[0].text}
              </button>
            </a>
          )}
        </div>
      </nav>

      {hasSections ? (
        sections.map((section, idx) => {
          const SectionComp = SECTION_MAP[section.type];
          if (!SectionComp) return null;
          return <SectionComp key={idx} section={section} accentColor={accentColor} product={product} />;
        })
      ) : (
        <SimpleLanding product={product} accentColor={accentColor} />
      )}
    </div>
  );
}
